/**
 * The TMDB adapter.
 *
 * One of `enrichment/providers/`' four adapters, and the seam ADR 0009 names. It lived inside
 * `app/api/lookup/tmdb/route.ts` until #407, which is why `enrich.ts` reached it over a cookie-less
 * HTTP call to the app's own origin and got a 401 every time (#78). A route file is an adapter and
 * owns nothing (ADR 0010); the route now keeps its `GET`, its `getUser()` guard, its 401, and a
 * delegation to `lookupMovie` below.
 *
 * **This module must not import `@/account/server`.** It is called in process by `enrich.ts`,
 * which runs wherever the enricher runs; the session check belongs to the route, not to the
 * provider.
 */

import { trackError } from '@/lib/errors'

const TMDB_API_BASE = 'https://api.themoviedb.org/3'
const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p'

interface TMDBSearchResult {
  id: number
  title: string
  original_title: string
  overview: string
  release_date?: string
  poster_path?: string
  genre_ids: number[]
  vote_average: number
}

interface TMDBSearchResponse {
  results: TMDBSearchResult[]
  total_results: number
}

interface TMDBMovie {
  id: number
  title: string
  original_title: string
  overview: string
  release_date?: string
  poster_path?: string
  genres: Array<{ id: number; name: string }>
  runtime?: number
  vote_average: number
}

interface TMDBCredits {
  crew: Array<{
    id: number
    name: string
    job: string
    department: string
  }>
}

// TMDB genre ID to name mapping (for search results that only have IDs)
const GENRE_MAP: Record<number, string> = {
  28: 'Action',
  12: 'Adventure',
  16: 'Animation',
  35: 'Comedy',
  80: 'Crime',
  99: 'Documentary',
  18: 'Drama',
  10751: 'Family',
  14: 'Fantasy',
  36: 'History',
  27: 'Horror',
  10402: 'Music',
  9648: 'Mystery',
  10749: 'Romance',
  878: 'Science Fiction',
  10770: 'TV Movie',
  53: 'Thriller',
  10752: 'War',
  37: 'Western',
}

export interface MovieResult {
  externalId: string
  title: string
  director?: string
  year?: number
  genres?: string[]
  posterUrl?: string
  overview?: string
  rating?: number
}

function getPosterUrl(
  posterPath: string | undefined | null,
  size: 'w92' | 'w185' | 'w342' | 'w500' = 'w342',
): string | undefined {
  if (!posterPath) return undefined
  return `${TMDB_IMAGE_BASE}/${size}${posterPath}`
}

function extractYear(releaseDate: string | undefined): number | undefined {
  if (!releaseDate) return undefined
  const year = parseInt(releaseDate.split('-')[0], 10)
  return isNaN(year) ? undefined : year
}

async function tmdbRequest(endpoint: string): Promise<Response> {
  const accessToken = process.env.TMDB_READ_ACCESS_TOKEN
  const apiKey = process.env.TMDB_API_KEY

  // Prefer Bearer token (v4 auth), fall back to API key (v3 auth)
  if (accessToken) {
    return fetch(`${TMDB_API_BASE}${endpoint}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })
  }

  if (apiKey) {
    const separator = endpoint.includes('?') ? '&' : '?'
    const url = `${TMDB_API_BASE}${endpoint}${separator}api_key=${apiKey}`
    return fetch(url)
  }

  throw new Error('TMDB_READ_ACCESS_TOKEN or TMDB_API_KEY must be configured')
}

async function searchMovies(
  query: string,
  year?: number,
): Promise<TMDBSearchResult[]> {
  let endpoint = `/search/movie?query=${encodeURIComponent(query)}`
  if (year) {
    endpoint += `&year=${year}`
  }

  const response = await tmdbRequest(endpoint)

  if (!response.ok) {
    trackError(new Error(`TMDB search failed: ${response.status}`), {
      provider: 'tmdb',
      query,
      status: response.status,
    })
    return []
  }

  const data: TMDBSearchResponse = await response.json()
  return data.results?.slice(0, 5) || []
}

async function getMovieDetails(movieId: number): Promise<TMDBMovie | null> {
  const response = await tmdbRequest(`/movie/${movieId}`)

  if (!response.ok) {
    trackError(new Error(`TMDB movie fetch failed: ${response.status}`), {
      provider: 'tmdb',
      movieId,
      status: response.status,
    })
    return null
  }

  return response.json()
}

async function getMovieCredits(movieId: number): Promise<TMDBCredits | null> {
  const response = await tmdbRequest(`/movie/${movieId}/credits`)

  if (!response.ok) {
    trackError(new Error(`TMDB credits fetch failed: ${response.status}`), {
      provider: 'tmdb',
      movieId,
      status: response.status,
    })
    return null
  }

  return response.json()
}

export interface MovieLookup {
  bestMatch: MovieResult | null
  alternatives: MovieResult[]
}

/**
 * The best match for a title, plus up to four alternatives.
 *
 * This is the whole of what the route used to compute inside its `GET`. Callers get the value; the
 * route wraps it in a `NextResponse` and owns the failure envelope.
 */
export async function lookupMovie(
  query: string,
  year?: number,
): Promise<MovieLookup> {
  const searchResults = await searchMovies(query, year)

  if (searchResults.length === 0) {
    return { bestMatch: null, alternatives: [] }
  }

  // Get details for best match
  const bestResult = searchResults[0]
  const [movieDetails, credits] = await Promise.all([
    getMovieDetails(bestResult.id),
    getMovieCredits(bestResult.id),
  ])

  // Find director from credits
  const director = credits?.crew?.find((c) => c.job === 'Director')?.name

  // Get genre names
  const genres =
    movieDetails?.genres?.map((g) => g.name) ||
    bestResult.genre_ids.map((id) => GENRE_MAP[id]).filter(Boolean)

  const bestMatch: MovieResult = {
    externalId: String(bestResult.id),
    title: movieDetails?.title || bestResult.title,
    director,
    year: extractYear(movieDetails?.release_date || bestResult.release_date),
    genres,
    posterUrl: getPosterUrl(
      movieDetails?.poster_path || bestResult.poster_path,
    ),
    overview: (movieDetails?.overview || bestResult.overview)?.slice(0, 500),
    rating: movieDetails?.vote_average || bestResult.vote_average,
  }

  const alternatives: MovieResult[] = searchResults.slice(1).map((r) => ({
    externalId: String(r.id),
    title: r.title,
    year: extractYear(r.release_date),
    genres: r.genre_ids.map((id) => GENRE_MAP[id]).filter(Boolean),
    posterUrl: getPosterUrl(r.poster_path, 'w92'),
    overview: r.overview?.slice(0, 200),
  }))

  return { bestMatch, alternatives }
}
